import { useState, useEffect, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { publicApi, type PublicTicket } from '@/api/public'
import { Spinner } from '@/components/ui/Spinner'

function fmt(n: number) {
  return new Intl.NumberFormat('fr-FR').format(n)
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })
}

function formatPhone(raw: string) {
  // "771234567" → "+221 77 123 45 67"
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 9) {
    return `+221 ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`
  }
  return `+221 ${raw}`
}

const TYPE_COLORS: Record<number, { badge: string; text: string; dot: string }> = {
  0: { badge: 'bg-gray-100',    text: 'text-gray-700',    dot: 'bg-gray-400' },
  1: { badge: 'bg-primary-50',  text: 'text-primary-700', dot: 'bg-primary-500' },
  2: { badge: 'bg-amber-50',    text: 'text-amber-700',   dot: 'bg-amber-500' },
  3: { badge: 'bg-green-50',    text: 'text-green-700',   dot: 'bg-green-500' },
}

function getTypeColor(typeName: string) {
  const lower = typeName.toLowerCase()
  if (lower.includes('vvip') || lower.includes('loge')) return TYPE_COLORS[2]
  if (lower.includes('vip')) return TYPE_COLORS[1]
  if (lower.includes('normal') || lower.includes('standard')) return TYPE_COLORS[0]
  return TYPE_COLORS[3]
}

export function TicketPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const [searchParams] = useSearchParams()

  const [ticket, setTicket] = useState<PublicTicket | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [whatsappTimeout, setWhatsappTimeout] = useState(false)

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const justPurchased = searchParams.get('success') === '1'

  const load = (id: string) =>
    publicApi.getTicket(id)
      .then(setTicket)
      .catch(() => setError('Ticket introuvable ou invalide.'))
      .finally(() => setLoading(false))

  useEffect(() => {
    if (!uuid) return
    load(uuid)
  }, [uuid])

  // Poll si whatsapp pas encore envoyé après 10s
  useEffect(() => {
    if (!ticket || ticket.whatsapp_sent) return
    if (pollRef.current) clearTimeout(pollRef.current)

    pollRef.current = setTimeout(async () => {
      if (!uuid) return
      try {
        const updated = await publicApi.getTicket(uuid)
        setTicket(updated)
        if (!updated.whatsapp_sent) setWhatsappTimeout(true)
      } catch {
        setWhatsappTimeout(true)
      }
    }, 10_000)

    return () => { if (pollRef.current) clearTimeout(pollRef.current) }
  }, [ticket, uuid])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner className="w-6 h-6" />
      </div>
    )
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
          </svg>
          <p className="text-gray-500 text-sm">{error ?? 'Ticket introuvable.'}</p>
        </div>
      </div>
    )
  }

  const typeColors = getTypeColor(ticket.ticket_type)

  return (
    <div className="min-h-screen bg-gray-100 py-0 sm:py-8">
      <div className="max-w-[440px] mx-auto bg-white sm:rounded-2xl sm:shadow-xl overflow-hidden">

        {/* === Header vert succès === */}
        <div className="bg-success-500 px-5 py-6 text-white">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <svg className="w-4.5 h-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <span className="text-sm font-semibold opacity-90">Commande confirmée</span>
          </div>
          <p className="text-xl font-bold leading-tight">{ticket.event_name}</p>
          <p className="text-sm opacity-80 mt-0.5 capitalize">{formatDate(ticket.event_date)}</p>
        </div>

        <div className="px-5 py-5 flex flex-col gap-5">

          {/* === Notification WhatsApp === */}
          {justPurchased && (
            ticket.whatsapp_sent ? (
              <div className="flex items-start gap-3 p-3.5 rounded-xl bg-green-50 border border-green-200">
                <svg className="w-5 h-5 text-green-500 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M11.933 0C5.354 0 0 5.354 0 11.933c0 2.106.554 4.086 1.523 5.797L0 24l6.418-1.494A11.875 11.875 0 0011.933 23.867C18.512 23.867 24 18.512 24 11.933 24 5.354 18.512 0 11.933 0zm0 21.867a9.875 9.875 0 01-5.034-1.373l-.361-.213-3.762.875.931-3.676-.237-.377A9.875 9.875 0 012.133 11.933C2.133 6.51 6.51 2.133 11.933 2.133S21.733 6.51 21.733 11.933 17.356 21.867 11.933 21.867z"/>
                </svg>
                <div>
                  <p className="text-sm font-semibold text-green-800">Ticket envoyé sur WhatsApp</p>
                  <p className="text-xs text-green-600 mt-0.5">
                    Votre ticket a été envoyé au {formatPhone(ticket.buyer_phone)}
                  </p>
                </div>
              </div>
            ) : whatsappTimeout ? (
              <div className="flex items-start gap-3 p-3.5 rounded-xl bg-warning-50 border border-warning-500/30">
                <svg className="w-4.5 h-4.5 text-warning-600 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <p className="text-sm text-warning-700">
                  L'envoi prend plus de temps que prévu. Vous pouvez télécharger votre ticket ci-dessous.
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3.5 rounded-xl bg-gray-50 border border-gray-200">
                <Spinner className="w-4 h-4 text-gray-400 shrink-0" />
                <p className="text-sm text-gray-600">Envoi du ticket sur WhatsApp en cours...</p>
              </div>
            )
          )}

          {/* WhatsApp status si page chargée directement (pas just purchased) */}
          {!justPurchased && (
            <div className="flex items-center gap-2">
              {ticket.whatsapp_sent ? (
                <>
                  <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span className="text-xs text-green-600 font-medium">Ticket envoyé sur WhatsApp</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 text-warning-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                  <span className="text-xs text-warning-600 font-medium">Envoi WhatsApp en cours...</span>
                </>
              )}
            </div>
          )}

          {/* === Badge type de ticket === */}
          <div className={`flex items-start gap-4 px-4 py-3.5 rounded-xl ${typeColors.badge}`}>
            <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${typeColors.dot}`} />
            <div>
              <p className={`text-lg font-bold ${typeColors.text}`}>{ticket.ticket_type}</p>
              {ticket.ticket_type_description && (
                <p className={`text-sm mt-0.5 ${typeColors.text} opacity-70`}>{ticket.ticket_type_description}</p>
              )}
            </div>
          </div>

          {/* === QR Code === */}
          <div className="flex flex-col items-center gap-3">
            {ticket.qr_code_url ? (
              <div className="p-4 bg-white border-2 border-gray-100 rounded-2xl">
                <img
                  src={ticket.qr_code_url}
                  alt="QR Code ticket"
                  className="w-48 h-48"
                />
              </div>
            ) : (
              <div className="w-56 h-56 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-2">
                <Spinner className="w-6 h-6 text-gray-300" />
                <p className="text-xs text-gray-400">QR code en génération...</p>
              </div>
            )}
            <p className="text-xs text-gray-400 font-mono">{ticket.uuid}</p>
          </div>

          {/* === Détails === */}
          <div className="flex flex-col gap-0 rounded-xl border border-gray-200 overflow-hidden">
            <Row label="Événement" value={ticket.event_name} />
            <Row label="Date" value={`${formatDate(ticket.event_date)} à ${ticket.event_time.slice(0, 5)}`} />
            <Row label="Lieu" value={ticket.location} />
            {ticket.buyer_name && <Row label="Acheteur" value={ticket.buyer_name} />}
            <Row label="Téléphone" value={formatPhone(ticket.buyer_phone)} />
            <Row
              label="Montant"
              value={`${ticket.quantity} × ${fmt(ticket.unit_price)} FCFA = ${fmt(ticket.total_price)} FCFA`}
              mono
            />
          </div>

          {/* === Téléchargement === */}
          {ticket.qr_code_url && (
            <a
              href={ticket.qr_code_url}
              download={`ticket-${ticket.uuid}.png`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3.5 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold text-sm text-center hover:border-gray-300 hover:bg-gray-50 transition-all duration-150 flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Télécharger le ticket
            </a>
          )}

          <p className="text-center text-xs text-gray-400 pb-2">
            Présentez ce QR code à l'entrée de l'événement.
          </p>
        </div>
      </div>
    </div>
  )
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-100 last:border-0">
      <span className="text-xs font-medium text-gray-500 shrink-0 w-24">{label}</span>
      <span className={`text-sm text-gray-900 text-right ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  )
}
